/**
 * Ads/Proposal tier → feature entitlements — the frontend mirror of the backend
 * layer (backend/api/services/entitlements.py, issue #1133, Option B).
 *
 * The backend already computes `enabled_features` on the workspace payload,
 * respecting the ADS_ENTITLEMENTS_ENABLED flag. So `enabledFeatures()` TRUSTS
 * that list when it is present — the payload is the runtime source of truth. The
 * matrix below is the parity mirror (kept honest by entitlements.test.ts, which
 * asserts it agrees with the backend's expected sets) plus the ship-dark
 * fallback for older payloads that predate the `enabled_features` field.
 */
import type { Workspace } from '@/types/auth';
import { resolveProductPackage } from '@/types/auth';

export const ADS_FEATURES = {
  PROPOSAL: 'F1',
  CAMPAIGN_BUILDER: 'F2',
  WEEKLY_OPTIMIZATION: 'F3',
  OPERATOR_DASHBOARDS: 'F4',
  CLIENT_ADS_VIEW: 'F5',
  MCC_OVERVIEW: 'F6',
  COMPETITOR_INSIGHTS: 'F7',
  PMAX: 'F8',
  LEAD_SCORING: 'F9',
  // B0: deeper half of the client Ads Performance view (per-keyword drill-down
  // + system pause-suggestions), unlocked at B2B. Mirrors the backend enum.
  ADVANCED_CLIENT_ANALYTICS: 'F10',
} as const;

export type AdsFeature = (typeof ADS_FEATURES)[keyof typeof ADS_FEATURES];

export const ALL_ADS_FEATURES: AdsFeature[] = Object.values(ADS_FEATURES);

export type AdsTier = 1 | 2 | 3;
export const TIER_DTC: AdsTier = 1;
export const TIER_B2B: AdsTier = 2;
export const TIER_ENTERPRISE: AdsTier = 3;

/** Relabels the old 1·Standard / 2·Managed / 3·Premium copy. */
export const TIER_LABELS: Record<AdsTier, string> = {
  1: 'DTC',
  2: 'B2B',
  3: 'Enterprise',
};

// Per-tier ADDITIONS, accumulated below into the superset ladder — mirrors
// _TIER_ADDS on the backend so the invariant (tier N ⊆ tier N+1) is structural.
const TIER_ADDS: Record<AdsTier, AdsFeature[]> = {
  1: [
    ADS_FEATURES.CAMPAIGN_BUILDER,
    ADS_FEATURES.WEEKLY_OPTIMIZATION,
    ADS_FEATURES.OPERATOR_DASHBOARDS,
    ADS_FEATURES.CLIENT_ADS_VIEW,
  ],
  2: [
    ADS_FEATURES.PROPOSAL,
    ADS_FEATURES.COMPETITOR_INSIGHTS,
    ADS_FEATURES.ADVANCED_CLIENT_ANALYTICS,
  ],
  3: [ADS_FEATURES.MCC_OVERVIEW, ADS_FEATURES.PMAX, ADS_FEATURES.LEAD_SCORING],
};

export const TIER_FEATURE_MATRIX: Record<AdsTier, AdsFeature[]> = (() => {
  const matrix = {} as Record<AdsTier, AdsFeature[]>;
  const acc: AdsFeature[] = [];
  ([1, 2, 3] as AdsTier[]).forEach((tier) => {
    acc.push(...TIER_ADDS[tier]);
    matrix[tier] = [...acc];
  });
  return matrix;
})();

/** Coerce any stored tier to 1..3, failing to DTC. Mirrors normalize_tier(). */
export function resolveTier(value: number | null | undefined): AdsTier {
  return value === 2 || value === 3 ? value : TIER_DTC;
}

/**
 * Compute the feature set from package + tier — the matrix. This is the parity
 * mirror of the backend; at runtime prefer the payload's `enabled_features`
 * (see enabledFeatures()), which already reflects the live flag.
 */
export function computeEnabledFeatures(
  productPackage: string | null | undefined,
  tier: number | null | undefined,
): AdsFeature[] {
  if (resolveProductPackage(productPackage) === 'seo') return [];
  return TIER_FEATURE_MATRIX[resolveTier(tier)];
}

/**
 * The features enabled for a workspace. TRUSTS the backend-computed
 * `enabled_features` when present — including an empty array, which is a valid
 * "nothing" for an seo-only tenant. Only a genuinely absent field (older
 * payload) falls back to "everything" (ship dark), so nothing is hidden until
 * the backend starts sending a restricted list.
 */
export function enabledFeatures(
  workspace: Workspace | null | undefined,
): Set<AdsFeature> {
  const fromPayload = workspace?.enabled_features;
  if (Array.isArray(fromPayload)) {
    return new Set(fromPayload as AdsFeature[]);
  }
  return new Set(ALL_ADS_FEATURES);
}

export function hasFeature(
  workspace: Workspace | null | undefined,
  feature: AdsFeature,
): boolean {
  return enabledFeatures(workspace).has(feature);
}
